/**
 * @fileoverview 헬프데스크 문의글 관련 React Query 커스텀 훅
 * 문의글 목록 조회, 상세 조회, 등록 기능을 위한 커스텀 훅들을 제공합니다.
 * React Query를 사용하여 서버 상태 관리와 캐싱을 처리합니다.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getArticle, getArticles, postArticle } from "@features/helpdesk/api";
import { ArticleIdType, GetArticlesType, GetArticleType, PaginationType } from "@features/helpdesk/model/getArticlesModel";
import { useNavigate } from "react-router";
import { PostArticlesType } from "@features/helpdesk/model/postArticlesModel";
import { useAuthStore } from "@core/stores/authStore";

/**
 * React Query 쿼리 키 상수 정의
 * 캐시 키 관리와 타입 안전성을 위한 상수들
 */
export const QUERY_KEYS = {
  /** 문의글 목록 조회 쿼리 키 */
  GET_ARTICLES: "getArticles",
  /** 문의글 상세 조회 쿼리 키 */
  GET_ARTICLE: "getArticle",
} as const;

/**
 * 문의글 목록을 조회하는 커스텀 훅
 * 페이지네이션 파라미터를 받아 문의글 목록을 조회합니다.
 *
 * @param params 페이지네이션 파라미터 (페이지 번호, 페이지 크기 등)
 * @returns 문의글 목록 쿼리 결과 (데이터, 로딩 상태, 에러 등)
 */
export const useArticles = (params: PaginationType) => {
  return useQuery<GetArticlesType | null, Error>({
    queryKey: [QUERY_KEYS.GET_ARTICLES, params],
    queryFn: () => getArticles(params),
  });
};

/**
 * 특정 문의글의 상세 정보를 조회하는 커스텀 훅
 * 문의글 ID를 받아 해당 문의글의 상세 정보를 조회합니다.
 *
 * @param params 문의글 ID를 포함한 파라미터 객체
 * @returns 문의글 상세 쿼리 결과 (데이터, 로딩 상태, 에러 등)
 */
export const useArticle = (params: { articleId: ArticleIdType }) => {
  return useQuery<GetArticleType | null, Error>({
    queryKey: [QUERY_KEYS.GET_ARTICLE, params],
    queryFn: () => getArticle(params),
  });
};

/**
 * 새로운 문의글을 등록하는 뮤테이션 커스텀 훅
 * 문의글 등록 후 캐시 무효화와 성공 페이지로의 리디렉션을 처리합니다.
 *
 * @returns 문의글 등록 뮤테이션 객체 (mutate 함수, 로딩 상태, 에러 등)
 */
export const usePostArticle = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isLogin } = useAuthStore();

  return useMutation<GetArticleType, Error, PostArticlesType>({
    mutationFn: (data: PostArticlesType) => postArticle(data, isLogin),
    onSuccess: () => {
      // 성공 시 문의 내역 목록 캐시 무효화
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_ARTICLES],
      });
      // 성공 페이지로 이동
      navigate("/helpdesk/contact/complete", { replace: true });
    },
  });
};
